import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../Modal';
import FileDropZone from '../FileDropZone';
import AttachmentVoidModal from '../AttachmentVoidModal';
import { AttachmentVoidPill, AttachmentVoidReason } from '../AttachmentVoidNote';
import { useAttachmentVoid } from '../../hooks/useAttachmentVoid';
import { isVoided } from '../../api/attachmentVoid';
import { useAuth } from '../../context/AuthContext';
import { formatDateTimePT } from '../../utils/timezone';
import {
  addPurchaseOrderNote,
  canReceivePurchaseOrder,
  fetchPurchaseOrder,
  purchaseOrderAttachmentUrl,
  unvoidPurchaseOrderAttachment,
  uploadPurchaseOrderAttachment,
  voidPurchaseOrderAttachment,
  type PurchaseOrderAttachmentKind,
  type PurchaseOrderAttachmentRow,
  type PurchaseOrderDetail,
  type PurchaseOrderItemRow,
  type PurchaseOrderRow,
} from '../../api/purchaseOrders';
import PurchaseOrderStatusPill, { PurchaseOrderApprovalTag } from './PurchaseOrderStatusPill';
import './PurchaseOrders.css';

/**
 * Everything on one purchase order: the template fields, the line items, the
 * receiving record, the waybill, notes, and attachments (the generated PDF among them).
 *
 * Editing, receiving, stocking and cancelling are raised to the parent so the same
 * dialogs serve both this modal and the action buttons in the tables.
 */

function money(value: number): string {
  return `$${(Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fileSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="po-detail-field">
      <dt>{label}</dt>
      <dd>{value == null || value === '' ? '—' : value}</dd>
    </div>
  );
}

interface PurchaseOrderDetailModalProps {
  purchaseOrderId: number;
  onClose: () => void;
  /** Called whenever this modal changes something the tables display. */
  onChanged: () => void;
  onRequestReceive: (po: PurchaseOrderRow, items: PurchaseOrderItemRow[]) => void;
  /** Stock lines already received, without recording another receipt. */
  onRequestStock: (po: PurchaseOrderRow, items: PurchaseOrderItemRow[]) => void;
  onRequestCancel: (po: PurchaseOrderRow) => void;
  onRequestReopen: (po: PurchaseOrderRow) => void;
  onRequestEdit: (po: PurchaseOrderRow, items: PurchaseOrderItemRow[]) => void;
}

const PurchaseOrderDetailModal: React.FC<PurchaseOrderDetailModalProps> = ({
  purchaseOrderId,
  onClose,
  onChanged,
  onRequestReceive,
  onRequestStock,
  onRequestCancel,
  onRequestReopen,
  onRequestEdit,
}) => {
  const { user } = useAuth();
  const [detail, setDetail] = useState<PurchaseOrderDetail | null>(null);
  const [loadError, setLoadError] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  /** Which section's drop zone is uploading, if any. */
  const [uploading, setUploading] = useState<PurchaseOrderAttachmentKind | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await fetchPurchaseOrder(purchaseOrderId));
      setLoadError('');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load the purchase order.');
    }
  }, [purchaseOrderId]);

  useEffect(() => {
    void load();
  }, [load]);

  const voidControl = useAttachmentVoid<PurchaseOrderAttachmentRow>({
    noun: 'attachment',
    label: (row) => row.original_file_name,
    onVoid: async (row, reason) => {
      await voidPurchaseOrderAttachment(purchaseOrderId, row.id, reason, user?.id ?? null);
      await load();
      onChanged();
    },
    onUnvoid: async (row) => {
      await unvoidPurchaseOrderAttachment(purchaseOrderId, row.id, user?.id ?? null);
      await load();
      onChanged();
    },
    onError: setActionError,
  });

  const handleUpload = async (files: File[], kind: PurchaseOrderAttachmentKind) => {
    if (files.length === 0) return;
    setUploading(kind);
    setActionError('');
    try {
      for (const file of files) {
        await uploadPurchaseOrderAttachment(purchaseOrderId, file, { userId: user?.id ?? null, kind });
      }
      await load();
      onChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to upload the file.');
    } finally {
      setUploading(null);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = noteDraft.trim();
    if (!text) return;
    setNoteBusy(true);
    setActionError('');
    try {
      await addPurchaseOrderNote(purchaseOrderId, text, user?.id ?? null);
      setNoteDraft('');
      await load();
      onChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add the note.');
    } finally {
      setNoteBusy(false);
    }
  };

  if (loadError) {
    return (
      <Modal isOpen onClose={onClose} title="Purchase Order" wide>
        <div className="modal-body">
          <p className="create-order-error" role="alert">{loadError}</p>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={onClose}>Close</button>
        </div>
      </Modal>
    );
  }

  if (!detail) {
    return (
      <Modal isOpen onClose={onClose} title="Purchase Order" wide>
        <div className="modal-body"><p className="page-subtitle">Loading…</p></div>
      </Modal>
    );
  }

  const po = detail.purchaseOrder;
  // The generated PDF sorts first from the server, so this is it when present.
  const generatedPdf = detail.attachments.find((a) => a.is_generated_pdf && !isVoided(a));
  // Voided files sink to the bottom but stay visible and downloadable.
  const sorted = [...detail.attachments].sort((a, b) => Number(isVoided(a)) - Number(isVoided(b)));
  const waybills = sorted.filter((a) => a.kind === 'waybill');
  const attachments = sorted.filter((a) => a.kind !== 'waybill');
  const hasOutstanding = detail.items.some((i) => i.qty_outstanding > 0);
  const canReceive = canReceivePurchaseOrder(po);
  const awaitingApproval =
    !canReceive && po.status !== 'Cancelled' && (hasOutstanding || po.awaiting_inventory_count > 0);

  const renderFile = (att: PurchaseOrderAttachmentRow) => (
    <li key={att.id} className={isVoided(att) ? 'po-attachment po-attachment--void' : 'po-attachment'}>
      <div className="po-attachment-main">
        <a
          href={purchaseOrderAttachmentUrl(po.id, att.id)}
          target="_blank"
          rel="noreferrer"
          className="po-attachment-name"
        >
          {att.original_file_name}
        </a>
        {att.is_generated_pdf ? <span className="po-product-tag">Generated PO</span> : null}
        {isVoided(att) ? <AttachmentVoidPill /> : null}
        <span className="po-attachment-meta">
          {[fileSize(att.file_size), att.uploaded_by, formatDateTimePT(att.created_at)].filter(Boolean).join(' · ')}
        </span>
        <AttachmentVoidReason attachment={att} />
      </div>
      <div className="po-attachment-actions">
        {isVoided(att) ? (
          <button
            type="button"
            className="po-secondary-btn"
            onClick={() => voidControl.unvoid(att)}
            disabled={voidControl.busy}
          >
            Unvoid
          </button>
        ) : (
          <button
            type="button"
            className="po-danger-btn"
            onClick={() => voidControl.requestVoid(att)}
            disabled={voidControl.busy}
          >
            Void
          </button>
        )}
      </div>
    </li>
  );

  return (
    <>
      <Modal isOpen onClose={onClose} title={`${po.po_number} · ${po.vendor_name}`} extraWide>
        <div className="modal-body">
          <div className="po-detail-banner">
            <PurchaseOrderStatusPill status={po.display_status} />
            {awaitingApproval ? <PurchaseOrderApprovalTag approval={po.approval_status} /> : null}
            {po.receiving_status ? (
              <span className="po-receiving-status">{po.receiving_status}</span>
            ) : null}
            {po.awaiting_inventory_count > 0 ? (
              <span className="po-awaiting-tag">
                {po.awaiting_inventory_count} {po.awaiting_inventory_count === 1 ? 'line' : 'lines'} awaiting inventory
              </span>
            ) : null}
            {generatedPdf ? (
              <a
                className="po-secondary-btn po-pdf-link"
                href={purchaseOrderAttachmentUrl(po.id, generatedPdf.id)}
                target="_blank"
                rel="noreferrer"
              >
                Open PDF
              </a>
            ) : null}
          </div>

          {po.status === 'Cancelled' && po.cancel_reason ? (
            <p className="po-cancel-reason">Cancelled: {po.cancel_reason}</p>
          ) : null}

          {awaitingApproval ? (
            <p className="po-approval-note" role="status">
              {po.approval_status === 'Rejected'
                ? 'This purchase order was rejected, so nothing on it can be received. Use Edit to approve it, or cancel it.'
                : 'This purchase order is waiting for approval. Nothing on it can be received until it is approved. Use Edit to set its approval status to Approved.'}
            </p>
          ) : null}

          <h4 className="po-detail-heading">Order</h4>
          <dl className="po-detail-fields">
            <Field label="PO Number" value={po.po_number} />
            <Field label="Date Issued" value={po.date_issued} />
            <Field label="Requested By" value={po.requested_by} />
            <Field label="Approval Status" value={po.approval_status} />
            <Field label="Approved By" value={po.approved_by} />
            <Field label="Approval Date" value={po.approved_date} />
            <Field label="Expected Delivery" value={po.expected_delivery_date} />
            <Field label="ETA" value={po.eta} />
            <Field label="Tracking Number" value={po.carrier_tracking} />
          </dl>

          <div className="po-detail-columns">
            <div>
              <h4 className="po-detail-heading">Vendor</h4>
              <dl className="po-detail-fields po-detail-fields--stacked">
                <Field label="Vendor Name" value={po.vendor_name} />
                <Field label="Address" value={po.vendor_address} />
                <Field label="Contact Person" value={po.vendor_contact_person} />
                <Field label="Phone / Email" value={po.vendor_phone_email} />
                <Field label="Payment Terms" value={po.payment_terms} />
              </dl>
            </div>
            <div>
              <h4 className="po-detail-heading">Ship To</h4>
              <dl className="po-detail-fields po-detail-fields--stacked">
                <Field label="Ship To" value={po.ship_to_name} />
                <Field label="Address" value={po.ship_to_address} />
                <Field label="Attn" value={po.ship_to_attn} />
                <Field label="Phone / Email" value={po.ship_to_phone_email} />
                <Field label="Requested Delivery" value={po.requested_delivery} />
              </dl>
            </div>
          </div>

          <h4 className="po-detail-heading">Items</h4>
          <div className="po-table-wrapper">
            <table className="po-table">
              <thead>
                <tr>
                  <th className="po-col-num">Item #</th>
                  <th>Description</th>
                  <th className="po-col-num">Qty</th>
                  <th className="po-col-num">Received</th>
                  <th className="po-col-num">Unit Price</th>
                  <th className="po-col-num">Line Total</th>
                  <th>Inventory</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.map((item) => (
                  <tr key={item.id}>
                    <td className="po-col-num">{item.line_no}</td>
                    <td>
                      {item.description}
                      {item.is_custom ? <span className="po-custom-tag">Custom</span> : null}
                      {item.product_name ? <span className="po-product-tag">{item.product_name}</span> : null}
                    </td>
                    <td className="po-col-num">{item.qty}</td>
                    <td className="po-col-num">{item.qty_received}</td>
                    <td className="po-col-num">{money(item.unit_price)}</td>
                    <td className="po-col-num">{money(item.line_total)}</td>
                    <td>
                      {item.qty_added_to_inventory > 0 && item.qty_added_to_inventory < item.qty_received
                        ? `Added ${item.qty_added_to_inventory} of ${item.qty_received}`
                        : item.added_to_inventory_at
                          ? `Added ${formatDateTimePT(item.added_to_inventory_at)}`
                          : item.qty_received > 0
                            ? 'Awaiting'
                            : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan={5}>Subtotal</td><td className="po-col-num">{money(po.subtotal)}</td><td /></tr>
                <tr><td colSpan={5}>Shipping / Freight</td><td className="po-col-num">{money(po.shipping)}</td><td /></tr>
                <tr><td colSpan={5}>Processing Fee</td><td className="po-col-num">{money(po.processing_fee)}</td><td /></tr>
                <tr><td colSpan={5}>Tax</td><td className="po-col-num">{money(po.tax)}</td><td /></tr>
                <tr className="po-table-total-row">
                  <td colSpan={5}>Total</td><td className="po-col-num">{money(po.total)}</td><td />
                </tr>
                {po.initial_estimated_cost != null ? (
                  <tr className="po-table-estimate-row">
                    <td colSpan={5}>Initial Estimated Cost</td>
                    <td className="po-col-num">{money(po.initial_estimated_cost)}</td>
                    <td />
                  </tr>
                ) : null}
              </tfoot>
            </table>
          </div>

          {po.notes_terms ? (
            <>
              <h4 className="po-detail-heading">Notes / Terms</h4>
              <p className="po-detail-prose">{po.notes_terms}</p>
            </>
          ) : null}

          {po.date_received ? (
            <>
              <h4 className="po-detail-heading">Receiving</h4>
              <dl className="po-detail-fields">
                <Field label="Date Received" value={po.date_received} />
                <Field label="Qty Ordered" value={po.qty_ordered} />
                <Field label="Qty Received" value={po.qty_received} />
                <Field
                  label="Qty Variance"
                  value={po.qty_variance === 0 ? '0' : po.qty_variance > 0 ? `+${po.qty_variance}` : po.qty_variance}
                />
                <Field label="Condition on Arrival" value={po.condition_on_arrival} />
                <Field label="Inspection Result" value={po.inspection_result} />
                <Field label="Received By" value={po.received_by} />
              </dl>
              {po.discrepancy_notes ? (
                <p className="po-detail-prose">
                  <strong>Discrepancy / damage: </strong>
                  {po.discrepancy_notes}
                </p>
              ) : null}
            </>
          ) : null}

          <h4 className="po-detail-heading">Waybill</h4>
          {waybills.length === 0 ? (
            <p className="page-subtitle">No waybill yet.</p>
          ) : (
            <ul className="po-attachment-list">{waybills.map(renderFile)}</ul>
          )}

          <FileDropZone
            onFiles={(files) => void handleUpload(files, 'waybill')}
            multiple
            disabled={uploading != null}
            variant="compact"
            prompt={uploading === 'waybill' ? 'Uploading…' : undefined}
            hint="The carrier's waybill or bill of lading for this shipment."
          />

          <h4 className="po-detail-heading">Attachments</h4>
          {attachments.length === 0 ? (
            <p className="page-subtitle">No attachments yet.</p>
          ) : (
            <ul className="po-attachment-list">{attachments.map(renderFile)}</ul>
          )}

          <FileDropZone
            onFiles={(files) => void handleUpload(files, 'attachment')}
            multiple
            disabled={uploading != null}
            variant="compact"
            prompt={uploading === 'attachment' ? 'Uploading…' : undefined}
            hint="Quotes, invoices, packing slips — anything that belongs with this PO."
          />

          <h4 className="po-detail-heading">Notes</h4>
          <form onSubmit={handleAddNote} className="po-note-form">
            <textarea
              className="form-textarea"
              rows={2}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Add a note about this purchase order…"
              disabled={noteBusy}
              aria-label="New note"
            />
            <button type="submit" className="po-secondary-btn" disabled={noteBusy || !noteDraft.trim()}>
              {noteBusy ? 'Adding…' : 'Add note'}
            </button>
          </form>
          {detail.notes.length === 0 ? (
            <p className="page-subtitle">No notes yet.</p>
          ) : (
            <ul className="po-note-list">
              {detail.notes.map((note) => (
                <li key={note.id} className="po-note">
                  <p className="po-note-text">{note.note}</p>
                  <span className="po-note-meta">
                    {[note.submitted_by, formatDateTimePT(note.created_at)].filter(Boolean).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {actionError ? <p className="create-order-error" role="alert">{actionError}</p> : null}
        </div>

        <div className="modal-actions po-detail-actions">
          <button type="button" className="cancel-button" onClick={onClose}>Close</button>
          <button type="button" className="po-secondary-btn" onClick={() => onRequestEdit(po, detail.items)}>
            Edit
          </button>
          {po.status === 'Cancelled' ? (
            <button type="button" className="po-secondary-btn" onClick={() => onRequestReopen(po)}>
              Reopen
            </button>
          ) : (
            <>
              <button type="button" className="po-danger-btn" onClick={() => onRequestCancel(po)}>
                Cancel PO
              </button>
              {po.awaiting_inventory_count > 0 && (
                <button
                  type="button"
                  className={hasOutstanding ? 'po-secondary-btn' : 'po-primary-btn'}
                  disabled={!canReceive}
                  onClick={() => onRequestStock(po, detail.items)}
                >
                  Add to inventory
                </button>
              )}
              {hasOutstanding && (
                <button
                  type="button"
                  className="po-primary-btn"
                  disabled={!canReceive}
                  onClick={() => onRequestReceive(po, detail.items)}
                >
                  Received
                </button>
              )}
            </>
          )}
        </div>
      </Modal>

      <AttachmentVoidModal control={voidControl} elevated />
    </>
  );
};

export default PurchaseOrderDetailModal;
